import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Volume2 } from 'lucide-react';
import { FamilyMember } from '../../data/family';
interface MemoryPanelProps {
  member: FamilyMember | null;
  onClose: () => void;
}
function VoicePlayer({
  title,
  duration
}: {
  title: string;
  duration: string;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  return <div className="bg-warm-bg border border-warm-border rounded-2xl p-4 flex items-center gap-4" data-id="element-51">
      <button onClick={() => setIsPlaying(!isPlaying)} className="w-12 h-12 rounded-full bg-warm-cyan text-warm-bg flex items-center justify-center shrink-0 hover:scale-105 transition-transform" data-id="element-52">
        {isPlaying ? <Pause className="w-5 h-5 fill-current" data-id="element-53" /> : <Play className="w-5 h-5 fill-current ml-1" data-id="element-54" />}
      </button>

      <div className="flex-1 min-w-0" data-id="element-55">
        <p className="text-sm font-medium text-warm-text truncate mb-1" data-id="element-56">
          {title}
        </p>
        <div className="flex items-center gap-2 h-6" data-id="element-57">
          <div className="flex items-center gap-0.5 flex-1 h-full" data-id="element-58">
            {[...Array(30)].map((_, i) => <motion.div key={i} animate={{
            height: isPlaying ? [4, Math.random() * 16 + 4, 4] : 4,
            opacity: isPlaying ? 1 : 0.3
          }} transition={{
            repeat: Infinity,
            duration: 0.5 + Math.random() * 0.5,
            ease: 'easeInOut'
          }} className="w-1 bg-warm-cyan rounded-full" data-id="element-59" />)}
          </div>
          <span className="text-xs text-warm-muted font-mono" data-id="element-60">{duration}</span>
        </div>
      </div>
    </div>;
}
export function MemoryPanel({
  member,
  onClose
}: MemoryPanelProps) {
  return <AnimatePresence data-id="element-61">
      {member && <>
          {/* Mobile Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 md:hidden" data-id="element-62" />

          {/* Panel */}
          <motion.div initial={{
        x: '100%',
        y: 0
      }} animate={{
        x: 0,
        y: 0
      }} exit={{
        x: '100%',
        y: 0
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-x-0 bottom-0 top-20 md:top-0 md:bottom-0 md:left-auto md:right-0 md:w-[400px] bg-warm-surface border-t md:border-t-0 md:border-l border-warm-border z-50 overflow-y-auto hide-scrollbar rounded-t-3xl md:rounded-none shadow-warm" data-id="element-63">
            {/* Mobile Drag Handle */}
            <div className="w-full flex justify-center pt-3 pb-1 md:hidden" data-id="element-64">
              <div className="w-12 h-1.5 bg-warm-border rounded-full" data-id="element-65" />
            </div>

            <div className="p-6 pb-24 md:pb-6" data-id="element-66">
              <div className="flex items-start justify-between mb-6" data-id="element-67">
                <div data-id="element-68">
                  <motion.h2 initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.1
              }} className="text-3xl font-serif text-warm-text mb-1" data-id="element-69">
                    {member.name}
                  </motion.h2>
                  <motion.p initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.15
              }} className="text-warm-muted font-serif italic" data-id="element-70">
                    b. {member.birthYear}{' '}
                    {member.deathYear ? `- d. ${member.deathYear}` : ''}
                  </motion.p>
                </div>
                <button onClick={onClose} className="p-2 bg-warm-bg rounded-full text-warm-muted hover:text-warm-text transition-colors hidden md:block" data-id="element-71">
                  <X className="w-5 h-5" data-id="element-72" />
                </button>
              </div>

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="mb-8" data-id="element-73">
                <p className="text-warm-text leading-relaxed text-lg" data-id="element-74">
                  {member.bio}
                </p>
              </motion.div>

              {member.voiceNote && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="mb-8" data-id="element-75">
                  <div className="flex items-center gap-2 mb-3 text-warm-muted" data-id="element-76">
                    <Volume2 className="w-4 h-4" data-id="element-77" />
                    <span className="text-sm font-medium uppercase tracking-wider" data-id="element-78">
                      Voice Memory
                    </span>
                  </div>
                  <VoicePlayer title={member.voiceNote.title} duration={member.voiceNote.duration} data-id="element-79" />
                </motion.div>}

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-80">
                <div className="flex items-center gap-2 mb-3 text-warm-muted" data-id="element-81">
                  <span className="text-sm font-medium uppercase tracking-wider" data-id="element-82">
                    Photo Album
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-83">
                  {member.photos[0] && <img src={member.photos[0]} alt="" className="col-span-2 w-full h-48 object-cover rounded-2xl border border-warm-border" data-id="element-84" />}
                  {member.photos[1] && <img src={member.photos[1]} alt="" className="w-full h-32 object-cover rounded-2xl border border-warm-border" data-id="element-85" />}
                  {member.photos[2] && <img src={member.photos[2]} alt="" className="w-full h-32 object-cover rounded-2xl border border-warm-border" data-id="element-86" />}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}